import React, { useEffect } from 'react';
import { useSelector, connect } from 'react-redux';
import PropTypes from 'prop-types';

import { bindActionCreators } from 'redux';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { paginationShape } from '@bibliocommons/bc-prop-types';
import { withRouter } from 'app/helpers/routing';

import ProgressBar from 'app/components/shared/ProgressBar';
import { VIEW, SEARCH_TYPES } from 'app/constants/SearchConstants';
import { METADATA_CATEGORY_TYPE, SEARCH_ORIGIN, UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import displayBibShape from 'app/shapes/displayBib';
import { searchQueryShape } from 'app/shapes/search';
import {
  mapDisplayBibToAnalyticsBibEntity,
  mapSearchBibAvailabilityToDisplayAvailability
} from 'app/helpers/displayBib';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { trackContentClick, trackEventPdfCreate } from 'app/actions/AnalyticsGa4Actions';
import DrawerActions from 'app/actions/DrawerActions';
import SearchActions from 'app/actions/SearchActions';
import { eventShape } from 'app/shapes/events';
import {
  selectDisplayBibs,
  selectSearchResults,
  selectCalloutFeaturedEvent,
  selectSearchFacets,
  selectSearchQuery,
  selectActiveFilters,
  selectPagination,
  selectEventsRSSLink
} from 'app/selectors/events/EventsSearchSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import { activeFiltersShape, facetFieldsShape } from 'app/shapes/facets';
import EventsSearchPage from './EventsSearchPage';

const BIB_ROW_ITEM_LIMIT = 8;

export function EventsSearchPageContainer(props) {
  const { searchQuery, pagination, searchActions } = props;

  const intl = useIntl();
  const location = useLocation();
  const currentLibrary = useSelector(selectCurrentLibrary);
  const backToLinkHref = location.search ? `${location.pathname}${location.search}` : `${location.pathname}`;
  const resultsCount = pagination.get('count');
  const searchTerm = searchQuery.get('q');

  useEffect(() => {
    if (resultsCount === 0 && searchTerm) {
      searchActions.performCatalogSearch({
        limit: BIB_ROW_ITEM_LIMIT,
        query: searchTerm,
        searchType: SEARCH_TYPES.SMART,
        view: VIEW.GROUPED
      });
    }
  }, [resultsCount, searchTerm, searchQuery, searchActions]);

  const handlePdfCreateClick = eventCount => {
    const { trackPdfCreateClick } = props;

    trackPdfCreateClick({
      ui: {
        ui_content_total_count: eventCount
      }
    });
  };

  const trackCatalogTitleClick = ({ displayBib }) => {
    const { title } = displayBib;
    const { trackContentClick: dispatchTrackContentClick, bibEntities: bibEntitiesMap } = props;

    const bibEntity = bibEntitiesMap?.get(displayBib.metadataId)?.toJS?.();
    const displayAvailability = mapSearchBibAvailabilityToDisplayAvailability(bibEntity?.availability);
    const bib = mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability);

    dispatchTrackContentClick({
      bib,
      search: {
        search_origin: SEARCH_ORIGIN.USER_QUERY,
        search_type: SEARCH_TYPES.KEYWORD,
        search_query: searchTerm
      },
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
        ui_content_layout: VIEW.MEDIUM,
        ui_content_page: pagination?.get('page'),
        ui_content_total_count: pagination?.get('count')
      },
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_value: title
      }
    });
  };

  return (
    <div>
      <DocumentHeadContainer
        currentLibrary={currentLibrary}
        pageTitle={intl.formatMessage({ id: 'events_search_page_title' })}
        description={intl.formatMessage(
          { id: 'events_search_page_description' },
          { libraryName: currentLibrary.get('fullName') }
        )}
      />
      <ProgressBar active={props.isFetching} />
      <BackToLinkManager href={backToLinkHref} pageId="events" />
      <EventsSearchPage
        {...props}
        currentPageHref={backToLinkHref}
        handlePdfCreateClick={handlePdfCreateClick}
        trackCatalogTitleClick={trackCatalogTitleClick}
      />
    </div>
  );
}

EventsSearchPageContainer.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  drawer: ImmutablePropTypes.mapContains({
    open: PropTypes.bool
  }).isRequired,
  displayBibs: PropTypes.arrayOf(displayBibShape),
  events: ImmutablePropTypes.list.isRequired,
  calloutFeaturedEvent: eventShape,
  searchActions: PropTypes.shape({
    performCatalogSearch: PropTypes.func.isRequired
  }),
  searchQuery: searchQueryShape.isRequired,
  pagination: paginationShape.isRequired,
  facetFields: facetFieldsShape.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  singleBranch: PropTypes.bool,
  eventsRSSLink: PropTypes.string.isRequired,
  trackPdfCreateClick: PropTypes.func.isRequired,
  trackContentClick: PropTypes.func.isRequired,
  bibEntities: ImmutablePropTypes.map
};

const mapStateToProps = state => {
  const library = selectCurrentLibrary(state);

  return {
    isFetching: state.getIn(['events', 'eventsSearch', 'isFetching'], false),
    drawer: state.get('drawer'),
    displayBibs: selectDisplayBibs(state),
    events: selectSearchResults(state),
    calloutFeaturedEvent: selectCalloutFeaturedEvent(state),
    facetFields: selectSearchFacets(state),
    searchQuery: selectSearchQuery(state),
    activeFilters: selectActiveFilters(state),
    pagination: selectPagination(state),
    singleBranch: library.get('singleBranch'),
    eventsRSSLink: selectEventsRSSLink(state),
    hasFeaturedEvents: state.getIn(['events', 'eventsSearch', 'hasFeaturedEvents'], false),
    bibEntities: selectBibEntities(state)
  };
};

const mapDispatchToProps = dispatch => ({
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  searchActions: bindActionCreators(SearchActions, dispatch),
  trackPdfCreateClick: bindActionCreators(trackEventPdfCreate, dispatch),
  trackContentClick: bindActionCreators(trackContentClick, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventsSearchPageContainer));
